/**
 * FNA-specific Firestore storage.
 * Schema: Client (email unique) -> Form Data -> Report.
 * Each client has one form data and one report per agent.
 * Collections: fnaClients, fnaFormData, fnaReports
 */

import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  collection,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS, fnaClientDocId } from './firestore-schema';

function sanitize(obj) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitize);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || typeof v === 'function') continue;
    if (v !== null && typeof v === 'object' && !(v instanceof Date) && !(v?.toDate)) {
      out[k] = sanitize(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Upsert FNA client. Uses clientEmail as unique key per agent.
 * @param {{ userId, userEmail, clientEmail, client1, client2?, address?, children? }}
 */
export async function upsertFnaClient({ userId, userEmail, clientEmail, client1, client2, address, children }) {
  if (!db || !userId || !clientEmail) throw new Error('userId and clientEmail required');
  const clientEmailNorm = (clientEmail || '').toLowerCase().trim();
  const docId = fnaClientDocId(userId, clientEmailNorm);
  if (!docId) throw new Error('Invalid client email');
  const ref = doc(db, COLLECTIONS.FNA_CLIENTS, docId);
  await setDoc(ref, {
    userId,
    userEmail: (userEmail || '').toLowerCase().trim(),
    clientEmail: clientEmailNorm,
    client1: client1 || {},
    client2: client2 || null,
    address: address || '',
    children: Array.isArray(children) ? children : [],
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return docId;
}

/**
 * Save FNA form data for a client. One form per client per agent.
 */
export async function saveFnaFormData({ userId, userEmail, clientEmail, formData }) {
  if (!db || !userId || !clientEmail) throw new Error('userId and clientEmail required');
  const clientEmailNorm = (clientEmail || '').toLowerCase().trim();
  const docId = fnaClientDocId(userId, clientEmailNorm);
  if (!docId) throw new Error('Invalid client email');
  const ref = doc(db, COLLECTIONS.FNA_FORM_DATA, docId);
  const sanitized = sanitize(formData);
  await setDoc(ref, {
    userId,
    userEmail: (userEmail || '').toLowerCase().trim(),
    clientEmail: clientEmailNorm,
    formData: sanitized,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return docId;
}

/**
 * Get FNA form data for a client.
 */
export async function getFnaFormData({ userId, clientEmail }) {
  if (!db || !userId || !clientEmail) return null;
  const docId = fnaClientDocId(userId, (clientEmail || '').toLowerCase().trim());
  if (!docId) return null;
  const snap = await getDoc(doc(db, COLLECTIONS.FNA_FORM_DATA, docId));
  if (!snap.exists()) return null;
  return snap.data().formData || null;
}

/**
 * Save FNA report for a client. One report per client per agent.
 * Also upserts client record.
 */
export async function saveFnaReport({ userId, userEmail, clientEmail, reportData, clientSnapshot }) {
  if (!db || !userId || !clientEmail) throw new Error('userId and clientEmail required');
  const clientEmailNorm = (clientEmail || '').toLowerCase().trim();
  const docId = fnaClientDocId(userId, clientEmailNorm);
  if (!docId) throw new Error('Invalid client email');

  // Upsert client
  if (clientSnapshot?.client1) {
    await upsertFnaClient({
      userId,
      userEmail,
      clientEmail: clientEmailNorm,
      client1: clientSnapshot.client1,
      client2: clientSnapshot.client2,
      address: clientSnapshot.address,
      children: clientSnapshot.children,
    });
  }

  const ref = doc(db, COLLECTIONS.FNA_REPORTS, docId);
  await setDoc(ref, {
    userId,
    userEmail: (userEmail || '').toLowerCase().trim(),
    clientEmail: clientEmailNorm,
    reportData: sanitize(reportData),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return docId;
}

/**
 * Get FNA report by client doc ID (userId_sanitizedEmail).
 */
export async function getFnaReport(reportId) {
  if (!db || !reportId) return null;
  const snap = await getDoc(doc(db, COLLECTIONS.FNA_REPORTS, reportId));
  if (!snap.exists()) return null;
  const d = snap.data();
  return { id: snap.id, ...d, reportData: d.reportData, createdAt: d.createdAt, updatedAt: d.updatedAt };
}

/**
 * List FNA reports for an agent.
 * Must use a query (where userId == auth.uid) so Firestore rules allow the read.
 * getDocs(collection) fails because it would try to read all docs, and any doc
 * with a different userId fails the rule, causing "Missing or insufficient permissions".
 */
export async function listFnaReports({ userId, limitVal = 100 }) {
  if (!db || !userId) return [];
  try {
    const col = collection(db, COLLECTIONS.FNA_REPORTS);
    const q = query(col, where('userId', '==', userId), limit(limitVal));
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => {
      const data = d.data();
      return { id: d.id, ...data, reportData: data.reportData, createdAt: data.createdAt, updatedAt: data.updatedAt };
    });
    list.sort((a, b) => {
      const ta = a.updatedAt?.toDate?.()?.getTime() || a.createdAt?.toDate?.()?.getTime() || 0;
      const tb = b.updatedAt?.toDate?.()?.getTime() || b.createdAt?.toDate?.()?.getTime() || 0;
      return tb - ta;
    });
    return list;
  } catch (err) {
    console.error('listFnaReports failed:', err);
    throw err;
  }
}

/**
 * List FNA form data entries for an agent (clients with saved form/draft).
 */
export async function listFnaFormData({ userId }) {
  if (!db || !userId) return [];
  try {
    const col = collection(db, COLLECTIONS.FNA_FORM_DATA);
    const q = query(col, where('userId', '==', userId), limit(200));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return { id: d.id, ...data, formData: data.formData, updatedAt: data.updatedAt };
    });
  } catch (err) {
    console.warn('listFnaFormData failed:', err.message);
    return [];
  }
}

/**
 * Delete all FNA data for a client (from fnaClients, fnaFormData, fnaReports).
 * @param {string} clientDocId - Document ID (userId_sanitizedEmail)
 */
export async function deleteFnaClient(clientDocId) {
  if (!db || !clientDocId) throw new Error('db and clientDocId required');
  const refs = [
    doc(db, COLLECTIONS.FNA_CLIENTS, clientDocId),
    doc(db, COLLECTIONS.FNA_FORM_DATA, clientDocId),
    doc(db, COLLECTIONS.FNA_REPORTS, clientDocId),
  ];
  await Promise.all(refs.map((ref) => deleteDoc(ref).catch(() => {})));
}

/**
 * List FNA clients for an agent (clients who have form data or reports).
 */
export async function listFnaClients({ userId }) {
  const reports = await listFnaReports({ userId, limitVal: 200 });
  const clientIds = [...new Set(reports.map((r) => r.id))];
  const clients = [];
  for (const id of clientIds) {
    const snap = await getDoc(doc(db, COLLECTIONS.FNA_CLIENTS, id));
    if (snap.exists()) {
      const d = snap.data();
      clients.push({ id: snap.id, ...d });
    } else {
      const r = reports.find((x) => x.id === id);
      if (r?.reportData) {
        clients.push({
          id,
          clientEmail: r.clientEmail,
          client1: r.reportData.client1 || {},
          client2: r.reportData.client2,
          address: r.reportData.address,
        });
      }
    }
  }
  return clients;
}
